import {
  Avatar, Button, Drawer, Dropdown, Menu,
} from 'antd';
import {
  Link, useLocation, useParams, useSearchParams,
} from 'react-router-dom';
import React, { FC, useState } from 'react';
import { DownOutlined, UserOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { MenuProps } from 'antd';
import { UserInfo } from '@ynomia/client';
import { currentRegion, hostnameOptions } from '../../../config';
import {
  getMenuFromBootstrap,
  getMenuItemsFromBootstrap,
  getProjects,
  getUserInfo,
} from '../../../selectors';
import {
  LOGIN_HINT,
  PROJECT_ID_STORAGE_KEY,
  PROJECT_SLUG,
  REGION,
  USER_INITIATED,
} from '../../../config/constants';
import { getContextStores } from '../../../context';
import { client, getDecoratedPath } from '../../../services';
import styles from './styles.module.less';
import { Icon } from '..';

const SideMenu: FC = () => {
  /* Context  */
  const contextStores = getContextStores();

  /* Selectors */
  const menuData = getMenuFromBootstrap(contextStores);
  const menuItems = getMenuItemsFromBootstrap(contextStores);
  const location = useLocation();
  const userInfo = getUserInfo(contextStores);
  const projects = getProjects(contextStores);

  const { email, picture, name } = userInfo as UserInfo;
  const [searchParams] = useSearchParams();
  const { projectSlug } = useParams();
  const [open, setOpen] = useState(false);
  const selectedKey = menuItems.find(route => route.path === location.pathname);

  const onSwitchRegion = (urlRegionCode: string) => {
    searchParams.append(REGION, urlRegionCode);
    localStorage.setItem(LOGIN_HINT, email);
    localStorage.removeItem(PROJECT_ID_STORAGE_KEY);
    // Hard refresh page as we want to read from new localstorage value
    window.location.href = `/redirect?${searchParams}`;
  };

  const menuRegion: MenuProps['items'] = hostnameOptions.map(({ urlRegionCode, label }) => ({
    label,
    key: urlRegionCode,
    onClick: () => onSwitchRegion(urlRegionCode),
  }));

  const processedMenuData = menuData.map(({ menu }) => menu?.map((route) => {
    let { path } = route;
    if (projectSlug && route?.path.includes(PROJECT_SLUG)) {
      path = getDecoratedPath({ projectSlug }, path) ?? path;
    }
    return {
      key: route?.id,
      icon: <FontAwesomeIcon icon={route?.icon} />,
      label: <Link to={path}>{route?.label}</Link>,
    };
  })).flat();

  const currentRegionLabel = hostnameOptions.find(option => option.region === currentRegion)?.label;

  const onClose = () => {
    setOpen(false);
  };

  const renderFooter = () => (
    <div className={styles.footerContainer}>
      <Avatar
        className={styles.avatar}
        shape="circle"
        size="large"
        icon={<UserOutlined />}
        src={picture}
      />
      <div>
        <div className={styles.name}>{name}</div>
        <Button
          onClick={() => client.logout(USER_INITIATED)}
          type="link"
          className={styles.logoutButton}
        >
          Logout
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Button
        type="text"
        onClick={() => setOpen(true)}
        icon={<Icon name="menu" height="18" />}
        style={{ marginTop: 3 }}
      />
      <Drawer
        title={(
          <div className={styles.drawerTitle}>
            <Button type="text" onClick={() => setOpen(false)} icon={<Icon name="close" />} />
            <div className={styles.logo}>
              <Link to="/">
                <Icon name="ynomiaFullLogoDark" />
              </Link>
            </div>
          </div>
        )}
        placement="left"
        closable={false}
        style={{ height: '100%' }}
        onClose={onClose}
        width={310}
        open={open}
        footer={renderFooter()}
      >
        <div style={{ height: '92%' }}>
          <div style={{ paddingBottom: '24px' }}>
            <Menu
              mode="inline"
              items={processedMenuData}
              selectedKeys={[selectedKey?.id || '']}
              onClick={onClose}
            />
          </div>
          {userInfo?.projects?.length !== projects?.length
          && (
          <div className={styles.updateRegionContainer}>
            <div className={styles.updateRegionTitleContainer}>
              <span className={styles.updateRegionTitle}>Update Region</span>
            </div>
            <Dropdown menu={{ items: menuRegion, selectable: true }} trigger={['click']}>
              <div className={styles.menuRegion}>
                {currentRegionLabel}
                <DownOutlined />
              </div>
            </Dropdown>
          </div>
          )}
        </div>

      </Drawer>
    </>
  );
};

export default SideMenu;
